import { useState, useEffect, useRef } from "react";
import clsx from "clsx";

import "./funnel.css";
import { INACTIVE_COLOR, DEFAULT_COLORS } from "@/constants/colors";
import { NoDataView } from "../NoDataView";
import { Tooltip } from "@/components/molecules/Tooltip";
import { InfoIcon } from "@/components/icons";
import { WidgetMetricDef } from "@/modules/widgets/types";

interface FunnelProps {
    data?: {
        data?: Array<{ label: string; value: number; metricName: string; color?: string }>;
        error?: string;
    };
    formatValue?: (value: number) => string;
    colors?: string[];
    widgetMetricsDefs?: WidgetMetricDef[];
    hiddenSeries?: Set<string>;
}

function Funnel({ data, formatValue = (value) => value.toLocaleString(), colors = DEFAULT_COLORS, widgetMetricsDefs, hiddenSeries }: FunnelProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const minWidth = 220;
    const minHeight = 580;
    const [dimensions, setDimensions] = useState({ width: minWidth, height: minHeight });
    
    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                const { width } = containerRef.current.getBoundingClientRect();
                setDimensions({ 
                    width: Math.max(width, minWidth), 
                    height: minHeight 
                });
            }
        };
        
        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => {
            window.removeEventListener('resize', updateDimensions);
        };
    }, []);
    
    if (data?.error) {
        return <div className="py-5 px-10 text-center text-red-500">Error: {data.error}</div>;
    }

    if (!Array.isArray(data?.data) || !data?.data?.length) {
        return <NoDataView />;
    }

    // Filter hidden series and sort data by value descending (typical funnel behavior)
    const filteredData = hiddenSeries 
        ? data?.data.filter(item => !hiddenSeries.has(item.label))
        : data?.data;
    const sortedData = [...filteredData].sort((a, b) => (b?.value ?? 0) - (a?.value ?? 0));
    const calculatedHeight = sortedData.length * 150; // Increased spacing per segment
    const maxHeight = 600;
    const height = Math.min(Math.max(minHeight, calculatedHeight), maxHeight);
    const maxLabelLength = sortedData.reduce((acc, item) => Math.max(acc, item.label?.length ?? 0), 0);
    const labelSpaceEstimate = maxLabelLength * 8; // ~8px per char at 14px font size
    const infoIconSpace = widgetMetricsDefs ? 20 : 0;
    const paddingRight = Math.min(240, Math.max(100, labelSpaceEstimate + infoIconSpace + 32));
    const funnelWidth = dimensions.width - paddingRight;
    const segmentHeight = (height - (sortedData.length - 1) * 10) / sortedData.length;

    // Calculate evenly distributed widths for consistent funnel shape
    const minWidthRatio = 0.15;
    const widthStep = (1 - minWidthRatio) / sortedData.length;
    const segmentWidths = sortedData.map((_, idx) => (1 - idx * widthStep) * funnelWidth);
    segmentWidths.push(minWidthRatio * funnelWidth);
    
    const segments = sortedData.map((item, idx) => {
        const startWidth = segmentWidths[idx];
        const endWidth = segmentWidths[idx + 1];

        const startY = idx * segmentHeight;
        const endY = startY + segmentHeight;

        const x0 = 0;
        const x1Start = startWidth;
        const x1End = endWidth;

        const isLastSegment = idx === sortedData.length - 1;
        const curveIntensity = 0.45; // More pronounced curve
        const controlX1 = x1Start + (x1End - x1Start) * 0.15; // Move control points closer to center
        const controlY1 = startY + segmentHeight * curveIntensity;
        const controlX2 = x1End - (x1End - x1Start) * 0.15;
        const controlY2 = endY - segmentHeight * curveIntensity;

        /**
         * M: Move to left-top corner
         * L: Line to right-top corner
         * C: Very subtle cubic curve to right-bottom
         * L: Line back to left-bottom
         * Z: Close path
         */
        const pathD = `
      M ${x0} ${startY}
      L ${x1Start} ${startY}
      C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${x1End} ${endY}
      L ${x0} ${endY}
      Z
    `;

        const labelX = dimensions.width - 10;
        const lineStartX = endWidth;
        const valueY = endY - 30;
        const labelY = endY - 12;
        const isInactive = item.value === 0;
        const color = isInactive ? INACTIVE_COLOR : (item.color || colors[idx % colors.length]);

        const animationDelay = idx * 150;

        const tooltipContent = widgetMetricsDefs?.find((metric) => metric.name === item.metricName)?.tooltip;

        return (
            <g key={idx}>
                <path d={pathD} fill={color} className={clsx("funnel-path", isInactive && "opacity-50")} style={{ animationDelay: `${animationDelay}ms` }} />
                <text
                    x={labelX}
                    y={valueY}
                    textAnchor="end"
                    fill={color}
                    fontFamily="Roboto"
                    fontWeight="700"
                    fontSize="22px"
                    className="funnel-value"
                    style={{ animationDelay: `${animationDelay + 300}ms` }}
                >
                    {formatValue(item.value)}
                </text>
                <foreignObject
                    x={funnelWidth + 10}
                    y={labelY - 16}
                    width={Math.max(0, paddingRight - (widgetMetricsDefs ? 16 : 20))}
                    height={24}
                    className="funnel-label"
                    style={{ animationDelay: `${animationDelay + 400}ms` }}
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: 4,
                            fontFamily: "Roboto",
                            fontSize: 14,
                            color: "#2d3a3f",
                            lineHeight: "24px",
                            width: "100%",
                            height: "100%",
                        }}
                    >
                        <span style={{whiteSpace: 'nowrap'}}>{item.label}</span>
                        {tooltipContent && (
                            <Tooltip content={tooltipContent} variant="widgetTitle">
                                <InfoIcon />
                            </Tooltip>
                        )}
                    </div>
                </foreignObject>
                {!isLastSegment && (
                    <line
                        x1={lineStartX}
                        y1={endY}
                        x2={dimensions.width - 10}
                        y2={endY}
                        stroke="#d3e2e8"
                        strokeWidth="1"
                        className="funnel-connector"
                        style={{ animationDelay: `${animationDelay + 500}ms` }}
                    />
                )}
            </g>
        );
    });

    return (
        <div 
            ref={containerRef}
            className="funnel w-full overflow-hidden h-full max-w-[600px] pl-8 pr-8"
        >
            <svg width="100%" height="100%" viewBox={`0 0 ${dimensions.width} ${height}`} preserveAspectRatio="xMidYMin meet">
                {segments}
            </svg>
        </div>
    );
}

export default Funnel;
